import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BattleClient } from "./components/BattleClient";
import { BoardEditorPage } from "./components/BoardEditorPage";
import "./globals.css";
import "./game.css";
import "./board-editor.css";

const root = document.getElementById("root");

if (!root) throw new Error("Application root was not found");

const refreshViewportMetrics = () => {
  const viewport = window.visualViewport;
  const width = Math.max(1, Math.round(viewport?.width ?? window.innerWidth));
  const height = Math.max(1, Math.round(viewport?.height ?? window.innerHeight));
  const rootElement = document.documentElement;
  rootElement.style.setProperty("--app-viewport-width", `${width}px`);
  rootElement.style.setProperty("--app-viewport-height", `${height}px`);
  rootElement.style.setProperty("--app-viewport-scale", `${viewport?.scale ?? 1}`);
  rootElement.dataset.orientation = width >= height ? "landscape" : "portrait";
  if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0);
};

let viewportFrame: number | undefined;
const scheduleViewportRefresh = () => {
  if (viewportFrame !== undefined) window.cancelAnimationFrame(viewportFrame);
  viewportFrame = window.requestAnimationFrame(() => {
    viewportFrame = undefined;
    refreshViewportMetrics();
    // Safari can publish the old dimensions for one frame during rotation.
    window.requestAnimationFrame(refreshViewportMetrics);
  });
};

refreshViewportMetrics();
window.addEventListener("resize", scheduleViewportRefresh, { passive: true });
window.addEventListener("orientationchange", () => {
  scheduleViewportRefresh();
  window.setTimeout(scheduleViewportRefresh, 80);
  window.setTimeout(scheduleViewportRefresh, 280);
}, { passive: true });
window.visualViewport?.addEventListener("resize", scheduleViewportRefresh, { passive: true });
window.visualViewport?.addEventListener("scroll", scheduleViewportRefresh, { passive: true });
window.addEventListener("pageshow", scheduleViewportRefresh, { passive: true });
document.addEventListener("visibilitychange", scheduleViewportRefresh, { passive: true });
void document.fonts?.ready.then(scheduleViewportRefresh);

for (const eventName of ["contextmenu", "selectstart", "dragstart", "gesturestart"]) {
  document.addEventListener(eventName, (event) => event.preventDefault(), { passive: false });
}

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => {
      // The game remains fully usable when service workers are unavailable.
    });
  });
}

const isBoardEditor = new URL(window.location.href).searchParams.get("tool") === "board-editor";

createRoot(root).render(
  <StrictMode>
    {isBoardEditor ? <BoardEditorPage /> : <BattleClient />}
    <div aria-label="縦画面専用" className="mobile-portrait-lock" role="status">
      <div className="mobile-portrait-lock-card">
        <span aria-hidden="true" className="mobile-portrait-lock-icon">↕</span>
        <strong>縦画面でご利用ください</strong>
        <small>スマホ版は縦向き専用です</small>
      </div>
    </div>
  </StrictMode>,
);
